import { makeAutoObservable, runInAction } from 'mobx';
import { auth } from '../firebase/firebase';
import { User, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { MemberRole } from '../types/organisation';
import { hasRole } from '../utilities/roleUtils';
import { memberHasModeratorPrivileges } from '../utilities/orgAccess';
import { hasPlatformOrgAccess } from '../utilities/platformAdminAccess';
import { OrganisationManager } from '../models/OrganisationManager';
import userStore from './UserStore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { watchSessionExpiry } from '../utilities/sessionExpiry';

export class AuthenticationStore {
  user: User | null = null;
  loading = false;
  error: Error | null = null;
  facilitatorProfileIncomplete = false;
  facilitatorProfileData: { userId: string; email?: string; name?: string } | null = null;
  facilitatorComplianceIncomplete = false;
  facilitatorComplianceData: { userId: string; contractSigned: boolean; dbsNumberShared: boolean } | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Subscribe to Firebase auth state. Called once on the app singleton; tests can skip it. */
  subscribeToAuthChanges() {
    auth.onAuthStateChanged((user) => {
      void this.handleAuthStateChanged(user);
    });
    watchSessionExpiry();
  }

  setUser = (user: User | null) => {
    this.user = user;
  }

  private resolvePlatformAdminRole = async (
    uid: string
  ): Promise<'superAdmin' | 'admin' | null> => {
    try {
      const superAdminSnap = await getDoc(doc(db, 'SuperAdmins', 'ids'));
      const superAdminKeys = superAdminSnap.exists()
        ? (superAdminSnap.data().userKeys as string[] | undefined) ?? []
        : [];
      if (superAdminKeys.includes(uid)) return 'superAdmin';

      const orgAdminSnap = await getDoc(doc(db, 'OrgAdmins', 'ids'));
      const orgAdminKeys = orgAdminSnap.exists()
        ? (orgAdminSnap.data().userKeys as string[] | undefined) ?? []
        : [];
      if (orgAdminKeys.includes(uid)) return 'admin';
      return null;
    } catch (error) {
      console.error('Failed to resolve platform admin role:', error);
      return null;
    }
  };

  /**
   * Persist the session role plus any other dashboards this account can open
   * (other org roles, artist profile, SuperAdmins/OrgAdmins lists).
   */
  hydrateAuthenticatedUser = async (
    user: User,
    userRole: MemberRole | 'author',
    overrides?: {
      email?: string;
      displayName?: string;
      organisations?: Record<string, unknown>;
      hasAuthorProfile?: boolean;
      platformAdminRole?: 'superAdmin' | 'admin';
    }
  ) => {
    let organisations = overrides?.organisations;
    let hasAuthorProfile = overrides?.hasAuthorProfile;
    if (organisations === undefined || hasAuthorProfile === undefined) {
      const membership = await OrganisationManager.getUserLoginMembership(user.uid).catch(
        () => ({ organisations: {}, hasAuthorProfile: false })
      );
      organisations = organisations ?? membership.organisations;
      hasAuthorProfile = hasAuthorProfile ?? membership.hasAuthorProfile;
    }

    let platformAdminRole = overrides?.platformAdminRole;
    if (!platformAdminRole) {
      if (userRole === 'superAdmin' || userRole === 'admin') {
        platformAdminRole = userRole;
      } else {
        platformAdminRole = (await this.resolvePlatformAdminRole(user.uid)) ?? undefined;
      }
    }

    userStore.setUser({
      userId: user.uid,
      email: overrides?.email ?? user.email ?? '',
      displayName: overrides?.displayName ?? user.displayName ?? '',
      organisations: organisations as never,
      hasAuthorProfile: Boolean(hasAuthorProfile) || userRole === 'author',
      platformAdminRole,
      userRole,
    });
    await userStore.applyPreferredMembershipOrganisation();
  };

  /**
   * Keep Firebase auth and UserStore platform-admin claims aligned.
   * Stale localStorage roles previously let users into the board while Firestore
   * denied workStreams/roles (isOverhearPlatformAdmin).
   */
  private handleAuthStateChanged = async (user: User | null) => {
    console.info('[OVH:FacilitatorDash] Auth:state-changed', {
      uid: user?.uid ?? null,
      storedUserId: userStore.user?.userId,
      storedRole: userStore.user?.userRole,
    });
    this.user = user;

    if (!user) {
      return;
    }

    const stored = userStore.user;
    const storedRole = stored?.userRole;
    const isPlatformClaim = hasPlatformOrgAccess(storedRole);

    if (!isPlatformClaim) {
      if (stored && (!stored.userId || stored.userId === user.uid)) {
        console.info('[OVH:FacilitatorDash] Auth:hydrate-non-platform-user', { uid: user.uid });
        userStore.setUser({
          ...stored,
          userId: user.uid,
          email: stored.email || user.email || '',
          displayName: stored.displayName || user.displayName || '',
        });
      }
      return;
    }

    try {
      const superAdminRef = doc(db, 'SuperAdmins', 'ids');
      const superAdminSnap = await getDoc(superAdminRef);
      const superAdminKeys = superAdminSnap.exists()
        ? (superAdminSnap.data().userKeys as string[] | undefined) ?? []
        : [];

          if (superAdminKeys.includes(user.uid)) {
            console.info('[OVH:FacilitatorDash] Auth:confirmed-superAdmin', { uid: user.uid });
            userStore.setUser({
              userId: user.uid,
              email: user.email || stored?.email || '',
              displayName: user.displayName || stored?.displayName || '',
              organisations: stored?.organisations,
              hasAuthorProfile: stored?.hasAuthorProfile,
              platformAdminRole: 'superAdmin',
              userRole: 'superAdmin',
            });
            return;
          }

      const orgAdminRef = doc(db, 'OrgAdmins', 'ids');
      const orgAdminSnap = await getDoc(orgAdminRef);
      const orgAdminKeys = orgAdminSnap.exists()
        ? (orgAdminSnap.data().userKeys as string[] | undefined) ?? []
        : [];

      if (orgAdminKeys.includes(user.uid)) {
        userStore.setUser({
          userId: user.uid,
          email: user.email || stored?.email || '',
          displayName: user.displayName || stored?.displayName || '',
          organisations: stored?.organisations,
          hasAuthorProfile: stored?.hasAuthorProfile,
          platformAdminRole: 'admin',
          userRole: 'admin',
        });
        return;
      }

      console.warn(
        'Cleared stale platform-admin session: uid is not in SuperAdmins/ids or OrgAdmins/ids',
      );
      await signOut(auth);
      userStore.clearUser();
    } catch (error) {
      console.error('Failed to re-validate platform admin session:', error);
    }
  };

  setFacilitatorProfileIncomplete = (incomplete: boolean, profileData?: { userId: string; email?: string; name?: string }) => {
    this.facilitatorProfileIncomplete = incomplete;
    this.facilitatorProfileData = profileData || null;
  }

  setFacilitatorComplianceIncomplete = (incomplete: boolean, complianceData?: { userId: string; contractSigned: boolean; dbsNumberShared: boolean }) => {
    this.facilitatorComplianceIncomplete = incomplete;
    this.facilitatorComplianceData = complianceData || null;
  }

  /**
   * After Firebase auth (email/password or Google), resolve org/platform privileges
   * and populate UserStore for the requested role.
   */
  applyAuthenticatedUserRole = async (user: User, role: MemberRole) => {
      // Super Admin login tab: resolve superAdmin vs org-only admin from Firestore lists
      if (role === 'superAdmin') {
        try {
          const superAdminRef = doc(db, 'SuperAdmins', 'ids');
          const superAdminSnap = await getDoc(superAdminRef);
          const superAdminKeys = superAdminSnap.exists()
            ? (superAdminSnap.data().userKeys as string[] | undefined) ?? []
            : [];

          if (superAdminKeys.includes(user.uid)) {
            await this.hydrateAuthenticatedUser(user, 'superAdmin', {
              platformAdminRole: 'superAdmin',
            });
            return;
          }

          const orgAdminRef = doc(db, 'OrgAdmins', 'ids');
          const orgAdminSnap = await getDoc(orgAdminRef);
          const orgAdminKeys = orgAdminSnap.exists()
            ? (orgAdminSnap.data().userKeys as string[] | undefined) ?? []
            : [];

          if (orgAdminKeys.includes(user.uid)) {
            await this.hydrateAuthenticatedUser(user, 'admin', {
              platformAdminRole: 'admin',
            });
            return;
          }

          await signOut(auth);
          userStore.clearUser();
          throw new Error('You do not have super admin or org admin access');
        } catch (error) {
          if (userStore.user) {
            await signOut(auth);
            userStore.clearUser();
          }
          throw error;
        }
      }
      
      // Special handling for facilitators - they don't need organisation access
      if (role === 'facilitator') {
        // Check if this user has facilitator role in any organisation
        const organisations = await OrganisationManager.getUserOrganisations(user.uid);
        
        // Verify user has facilitator role in at least one organisation
        const orgValues = Object.values(organisations) as { roles?: MemberRole[]; role?: MemberRole }[];
        
        const hasFacilitatorRole = orgValues.some(org => {
          // Handle both new (roles array) and legacy (single role) formats
          if (org.roles) {
            return hasRole(org.roles, 'facilitator');
          }
          return org.role === 'facilitator';
        });
        
        if (!hasFacilitatorRole) {
          // Sign out if user doesn't have the facilitator role
          await signOut(auth);
          userStore.clearUser();
          throw new Error('You do not have facilitator access to any organisation');
        }
          
          // Check facilitator profile completeness and compliance
          const userDocRef = doc(db, 'Users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          let facilitatorEmail = user.email || '';
          let facilitatorName = '';
          let profileIncomplete = false;
          let complianceIncomplete = false;
          let contractSigned = false;
          let dbsNumberShared = false;
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const organisations = userData.Organisations || {};
            
            // Find facilitator organisations and get email from Organisations[orgId].email (encrypted)
            const facilitatorOrgIds = Object.keys(organisations).filter(orgId => {
              const orgData = organisations[orgId];
              return hasRole(orgData?.roles, 'facilitator');
            });
            
            // Get email from first facilitator organisation's email field (encrypted)
            if (facilitatorOrgIds.length > 0) {
              const firstFacilitatorOrg = organisations[facilitatorOrgIds[0]];
              facilitatorEmail = firstFacilitatorOrg?.email || user.email || '';
            } else {
            facilitatorEmail = userData.email || user.email || '';
            }
            
            facilitatorName = userData.userProfile?.userName || '';
            
            // Check if profile is incomplete
            // Note: encrypted emails are strings, so we check if they exist and are not empty
            if (!facilitatorEmail || facilitatorEmail.trim() === '' || facilitatorEmail === 'No email' || !facilitatorName.trim()) {
              profileIncomplete = true;
              runInAction(() => {
                this.setFacilitatorProfileIncomplete(true, {
                  userId: user.uid,
                  email: facilitatorEmail,
                  name: facilitatorName
                });
              });
            }

            // Check compliance requirements from Organisations[orgId].facilitatorInfo
            // Use first facilitator organisation's facilitatorInfo
            if (facilitatorOrgIds.length > 0) {
              const firstFacilitatorOrg = organisations[facilitatorOrgIds[0]];
              const facilitatorInfo = firstFacilitatorOrg?.facilitatorInfo || userData.facilitatorInfo || {};
              contractSigned = facilitatorInfo.contractSigned || false;
              dbsNumberShared = facilitatorInfo.dbsNumberShared || false;
            } else {
              // Fallback to root facilitatorInfo for backward compatibility
            contractSigned = userData.facilitatorInfo?.contractSigned || false;
            dbsNumberShared = userData.facilitatorInfo?.dbsNumberShared || false;
            }
            
            if (!contractSigned || !dbsNumberShared) {
              complianceIncomplete = true;
              runInAction(() => {
                this.setFacilitatorComplianceIncomplete(true, {
                  userId: user.uid,
                  contractSigned,
                  dbsNumberShared
                });
              });
            }
          } else {
            // User document doesn't exist, profile is definitely incomplete
            profileIncomplete = true;
            complianceIncomplete = true;
            contractSigned = false;
            dbsNumberShared = false;
            runInAction(() => {
              this.setFacilitatorProfileIncomplete(true, {
                userId: user.uid,
                email: facilitatorEmail,
                name: ''
              });
              this.setFacilitatorComplianceIncomplete(true, {
                userId: user.uid,
                contractSigned: false,
                dbsNumberShared: false
              });
            });
          }
        
        // Set user in UserStore with facilitator role
        await this.hydrateAuthenticatedUser(user, 'facilitator', {
          email: facilitatorEmail,
          displayName: user.displayName || facilitatorName,
          organisations,
        });

        return;
      }
      
      // Special handling for wander writers - similar to facilitators
      if (role === 'wanderWriter') {
        // Check if this user has wander writer role in any organisation
        const organisations = await OrganisationManager.getUserOrganisations(user.uid);
        
        // Verify user has wander writer role in at least one organisation
        const orgValues = Object.values(organisations) as { roles?: MemberRole[]; role?: MemberRole }[];
        
        const hasWanderWriterRole = orgValues.some(org => {
          // Handle both new (roles array) and legacy (single role) formats
          if (org.roles) {
            return hasRole(org.roles, 'wanderWriter');
          }
          return org.role === 'wanderWriter';
        });
        
        if (!hasWanderWriterRole) {
          // Sign out if user doesn't have the wander writer role
          await signOut(auth);
          userStore.clearUser();
          throw new Error('You do not have wander writer access to any organisation');
        }
        
        // Set user in UserStore with wanderWriter role
        await this.hydrateAuthenticatedUser(user, 'wanderWriter', {
          organisations,
        });

        return;
      }

      // Project admins: Firebase session only — ownership is verified by the login screen
      if (role === 'projectAdmin') {
        await this.hydrateAuthenticatedUser(user, 'projectAdmin');
        return;
      }

      // Participants / members: establish member session (org data loaded as needed)
      if (role === 'member') {
        const organisations = await OrganisationManager.getUserOrganisations(user.uid);
        await this.hydrateAuthenticatedUser(user, 'member', { organisations });
        if (Object.keys(organisations).length === 1) {
          await userStore.switchOrganisation(Object.keys(organisations)[0]);
        }
        return;
      }
      
      // For organisation roles (organisationAdmin, moderator, member)
      const organisations = await OrganisationManager.getUserOrganisations(user.uid);
      
      // Verify role matches the requested role or has higher privileges
      const orgValues = Object.values(organisations) as { roles?: MemberRole[]; role?: MemberRole }[];
      
      const hasRequiredRole = orgValues.some(org => {
        // Handle both new (roles array) and legacy (single role) formats
        const userRoles = org.roles || (org.role ? [org.role] : []);
        
        // Allow organisationAdmin to access moderator features
        if (role === 'moderator') {
          return memberHasModeratorPrivileges(userRoles);
        }
        return hasRole(userRoles, role);
      });
      
      if (!hasRequiredRole) {
        // Sign out if user doesn't have the required role
        await signOut(auth);
        userStore.clearUser();
        throw new Error(`You do not have ${role} access to any organisation`);
      }
      
      // Set user in UserStore with role
      await this.hydrateAuthenticatedUser(user, role, { organisations });
      
      // If there's only one Organisation, set it as current
      if (Object.keys(organisations).length === 1) {
        const orgId = Object.keys(organisations)[0];
        await userStore.switchOrganisation(orgId);
      }
  }

  signInWithRole = async (email: string, password: string, role: MemberRole) => {
    try {
      this.loading = true;
      this.error = null;

      // Sign in with Firebase
      let userCredential;
      try {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      } catch (firebaseError) {
        console.error('Firebase authentication error:', firebaseError);
        // Log specific firebase error code
        if (firebaseError instanceof Error) {
          console.error('Firebase error code:', (firebaseError as any).code);
        }
        throw firebaseError;
      }

      await this.applyAuthenticatedUserRole(userCredential.user, role);
      return userCredential;
    } catch (error) {
      if (error instanceof Error) {
        // Handle Firebase Auth errors
        if (error.message.includes('auth/user-not-found')) {
          this.error = new Error('No account found with this email');
        } else if (error.message.includes('auth/wrong-password')) {
          this.error = new Error('Incorrect password');
        } else if (error.message.includes('auth/invalid-email')) {
          this.error = new Error('Invalid email format');
        } else {
          this.error = error;
        }
      } else {
        this.error = new Error('An unexpected error occurred');
      }
      throw this.error;
    } finally {
      this.loading = false;
    }
  }

  signInWithGoogleRole = async (role: MemberRole) => {
    try {
      this.loading = true;
      this.error = null;
      const { signInWithGoogle } = await import('../auth/utils/socialAuth');
      const userCredential = await signInWithGoogle();
      await this.applyAuthenticatedUserRole(userCredential.user, role);
      return userCredential;
    } catch (error) {
      if (error instanceof Error) {
        this.error = error;
      } else {
        this.error = new Error('An unexpected error occurred');
      }
      throw this.error;
    } finally {
      this.loading = false;
    }
  }

  signOut = async () => {
    await signOut(auth);
    userStore.clearUser();
    // Reset facilitator profile and compliance state
    runInAction(() => {
      this.setFacilitatorProfileIncomplete(false);
      this.setFacilitatorComplianceIncomplete(false);
    });
  }

  get isAuthenticated() {
    return !!this.user;
  }

  // Add a flag to distinguish between superadmin and org admin logins
  private isSuperAdminLogin = false;

  setSuperAdminLogin(value: boolean) {
    this.isSuperAdminLogin = value;
  }
}

const authenticationStore = new AuthenticationStore();
authenticationStore.subscribeToAuthChanges();

export default authenticationStore;